import dayjs from "dayjs";

import {
  ICognitoMigrationPatient,
  ICognitoMigrationResult,
  ICognitoMigrationStatus,
} from "../../../@types/admin/AdminCognitoMigration";
import { toCsv } from "../../../utils/csv";

/**
 * The pure half of the Cognito Migration page (EN-5222): what a run is made
 * of, which rows it sends to the API, how each outcome reads, and the results
 * spreadsheet. Kept free of React so it can be tested without rendering.
 */

/** Why a selected row never reached the API. */
export type IRunSkipReason =
  | "already-migrated"
  | "no-email"
  | "unknown-state"
  | "cancelled";

export interface IRunItem {
  patient: ICognitoMigrationPatient;
  state: "pending" | "running" | "done";
  /** Set when the row was decided locally and never sent. */
  skipped?: IRunSkipReason;
  /** Set when the API answered, whatever the outcome. */
  result?: ICognitoMigrationResult;
  /** Set when the request itself failed (network, 403, 500). */
  requestError?: string;
}

export interface IRunSummary {
  total: number;
  processed: number;
  migrated: number;
  alreadyMigrated: number;
  failed: number;
  skipped: number;
}

export const STATUS_LABEL: Record<ICognitoMigrationStatus, string> = {
  NotMigrated: "Not in Cognito",
  Migrated: "In Cognito",
  PasswordNotSet: "In Cognito, no password",
  NoEmail: "No email",
  Unknown: "Unexpected state",
};

export function statusTone(
  status: ICognitoMigrationStatus
): "active" | "inactive" | "warning" | "error" {
  switch (status) {
    case "Migrated":
      return "active";
    case "NotMigrated":
      return "warning";
    case "PasswordNotSet":
      return "error";
    default:
      return "inactive";
  }
}

/**
 * Only these two states are sent to the API. A confirmed account already has
 * a password its owner chose — resetting it in bulk would lock them out — and
 * the other two have nothing to create. The API applies the same rule; this
 * saves the round trip and lets the results file say why the row was skipped.
 */
export function skipReasonFor(
  status: ICognitoMigrationStatus
): IRunSkipReason | undefined {
  switch (status) {
    case "NotMigrated":
    case "PasswordNotSet":
      return undefined;
    case "Migrated":
      return "already-migrated";
    case "NoEmail":
      return "no-email";
    default:
      return "unknown-state";
  }
}

export function isMigratable(status: ICognitoMigrationStatus): boolean {
  return skipReasonFor(status) === undefined;
}

/** The run's rows, in selection order, each already decided or pending. */
export function planRun(patients: ICognitoMigrationPatient[]): IRunItem[] {
  return patients.map((patient) => {
    const skipped = skipReasonFor(patient.cognitoStatus);
    return skipped
      ? { patient, state: "done", skipped }
      : { patient, state: "pending" };
  });
}

const SKIP_LABEL: Record<IRunSkipReason, string> = {
  "already-migrated": "Skipped - already in Cognito",
  "no-email": "Skipped - no email address",
  "unknown-state": "Skipped - unexpected Cognito state",
  cancelled: "Skipped - run cancelled",
};

/** The one-line result of a row, as both the screen and the file show it. */
export function itemResultLabel(item: IRunItem): string {
  if (item.skipped) return SKIP_LABEL[item.skipped];
  if (item.requestError) return "Failed";
  if (!item.result)
    return item.state === "running" ? "Migrating..." : "Waiting";
  switch (item.result.outcome) {
    case "Migrated":
      return "Migrated";
    case "AlreadyMigrated":
      return "Skipped - already in Cognito";
    default:
      return "Failed";
  }
}

/** What went wrong, or what to know, about a row. Empty when nothing did. */
export function itemDetail(item: IRunItem): string {
  if (item.skipped === "already-migrated") {
    return "The account already has a password. It was not changed.";
  }
  if (item.skipped === "unknown-state") {
    return "Check the account in the Cognito console.";
  }
  if (item.requestError) return item.requestError;
  if (item.result?.outcome === "Failed") return item.result.error ?? "";
  if (item.result?.outcome === "AlreadyMigrated") {
    return "The account already has a password. It was not changed.";
  }
  return "";
}

export function itemTone(
  item: IRunItem
): "active" | "inactive" | "warning" | "error" {
  if (item.skipped) return "inactive";
  if (item.requestError || item.result?.outcome === "Failed") return "error";
  if (item.result?.outcome === "Migrated") return "active";
  if (item.result?.outcome === "AlreadyMigrated") return "inactive";
  return "warning";
}

export function summarize(items: IRunItem[]): IRunSummary {
  const summary: IRunSummary = {
    total: items.length,
    processed: 0,
    migrated: 0,
    alreadyMigrated: 0,
    failed: 0,
    skipped: 0,
  };
  items
    .filter((item) => item.state === "done")
    .forEach((item) => {
      summary.processed += 1;
      if (item.skipped) {
        if (item.skipped === "already-migrated") summary.alreadyMigrated += 1;
        else summary.skipped += 1;
      } else if (item.requestError || item.result?.outcome === "Failed") {
        summary.failed += 1;
      } else if (item.result?.outcome === "AlreadyMigrated") {
        summary.alreadyMigrated += 1;
      } else if (item.result?.outcome === "Migrated") {
        summary.migrated += 1;
      }
    });
  return summary;
}

/** True when the run holds a password nobody has saved yet. */
export function hasUnsavedPasswords(items: IRunItem[]): boolean {
  return items.some((item) => Boolean(item.result?.password));
}

export const RESULTS_CSV_HEADERS = [
  "Patient ID",
  "User ID",
  "Name",
  "Email",
  "Ward",
  "Cognito status before",
  "Result",
  "New password",
  "Details",
];

/**
 * The results spreadsheet. Every selected row is in it — migrated rows with
 * their new password, the rest with the reason nothing was issued — so a site
 * coordinator reading the file knows which patients still use their old
 * password. Note `toCsv` escapes cells that start with a formula character;
 * the API's password alphabet avoids those, so passwords come through intact.
 */
export function buildResultsCsv(items: IRunItem[]): string {
  return toCsv(
    RESULTS_CSV_HEADERS,
    items.map((item) => [
      item.patient.patientId,
      item.patient.userId,
      item.patient.name,
      item.patient.email ?? "",
      item.result?.ward ?? item.patient.ward ?? "",
      STATUS_LABEL[item.result?.previousStatus ?? item.patient.cognitoStatus],
      itemResultLabel(item),
      item.result?.password ?? "",
      itemDetail(item),
    ])
  );
}

export function resultsFilename(hospitalName: string | undefined): string {
  const slug = (hospitalName ?? "hospital")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return `cognito-migration-${slug || "hospital"}-${dayjs().format(
    "YYYY-MM-DD-HHmm"
  )}.csv`;
}
